// PeopleBulkActions — the lifecycle tabs' selection-bar "Bulk actions" menu. A
// Button opens a Dropdown of the four attribute actions that apply to every
// selected person: Assign/Remove role and Add/Remove from structure. The flows
// themselves live in PeoplePage via the callbacks (open a modal / confirm,
// then POST company-users/bulk-action).

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dropdown, Icon } from '@/components/common';

interface PeopleBulkActionsProps {
  onAssignRole: () => void;
  onRemoveRole: () => void;
  onAddToStructure: () => void;
  onRemoveFromStructure: () => void;
  /** True when the selection includes an owner — role actions can't apply to owners. */
  disableRoleActions?: boolean;
}

const PeopleBulkActions = ({
  onAssignRole,
  onRemoveRole,
  onAddToStructure,
  onRemoveFromStructure,
  disableRoleActions = false,
}: PeopleBulkActionsProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const roleActionTitle = disableRoleActions ? t('people_bulk_owner_role_disabled') : undefined;

  // Close the menu, then run the action (which opens its modal/confirm).
  const pick = (run: () => void) => () => {
    setOpen(false);
    run();
  };

  return (
    <Dropdown
      open={open}
      onClose={() => setOpen(false)}
      align='start'
      width={220}
      portal
      trigger={
        <Button
          variant='secondary'
          size='sm'
          iconStart='list-checks'
          iconEnd={open ? 'chevron-up' : 'chevron-down'}
          aria-haspopup='menu'
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          {t('people_bulk_actions')}
        </Button>
      }
    >
      <button
        type='button'
        className='app-dropdown__item'
        role='menuitem'
        disabled={disableRoleActions}
        aria-disabled={disableRoleActions}
        title={roleActionTitle}
        onClick={pick(onAssignRole)}
      >
        <Icon name='shield-check' size={15} />
        <span className='app-dropdown__item-label'>{t('people_bulk_assign_role')}</span>
      </button>
      <button
        type='button'
        className='app-dropdown__item'
        role='menuitem'
        disabled={disableRoleActions}
        aria-disabled={disableRoleActions}
        title={roleActionTitle}
        onClick={pick(onRemoveRole)}
      >
        <Icon name='shield-off' size={15} />
        <span className='app-dropdown__item-label'>{t('people_bulk_remove_role')}</span>
      </button>

      <div className='app-dropdown__divider' role='separator' />

      <button
        type='button'
        className='app-dropdown__item'
        role='menuitem'
        onClick={pick(onAddToStructure)}
      >
        <Icon name='network' size={15} />
        <span className='app-dropdown__item-label'>{t('people_bulk_add_structure')}</span>
      </button>
      <button
        type='button'
        className='app-dropdown__item'
        role='menuitem'
        onClick={pick(onRemoveFromStructure)}
      >
        <Icon name='unlink' size={15} />
        <span className='app-dropdown__item-label'>{t('people_bulk_remove_structure')}</span>
      </button>
    </Dropdown>
  );
};

export default PeopleBulkActions;
